import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { atsApi } from '../services/atsApi';
import type { AtsImportFile, AtsImportJob, AtsImportJobSummary } from '../types';
import { buildAtsCandidateLink } from '../utils/candidateDeepLink';

const IMPORT_STATUS_LABELS: Record<string, string> = {
  created: 'Создан',
  queued: 'В очереди',
  processing: 'Обрабатывается',
  completed: 'Завершен',
  completed_with_errors: 'Завершен с ошибками',
  failed: 'Ошибка импорта',
  cancelled: 'Отменен',
};

const ACTIVE_IMPORT_STATUSES = new Set(['queued', 'processing']);
const CANCELABLE_IMPORT_STATUSES = new Set(['queued', 'processing']);
const RESTARTABLE_IMPORT_STATUSES = new Set([
  'queued',
  'processing',
  'failed',
  'cancelled',
  'completed',
  'completed_with_errors',
]);

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileError = (file: AtsImportFile): string | null => (
  file.error_message || file.error || null
);

const getFileWarnings = (file: AtsImportFile): string[] => {
  if (Array.isArray(file.warnings)) {
    return file.warnings.map(String).map((item) => item.trim()).filter(Boolean);
  }
  if (typeof file.warnings === 'string' && file.warnings.trim()) {
    return file.warnings.split(';').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const getJobFiles = (job: AtsImportJob): AtsImportFile[] => {
  const reportFiles = job.report?.files || [];
  const reportErrors = job.report?.errors || [];
  const merged = [...reportFiles, ...reportErrors];
  if (merged.length > 0) {
    return merged;
  }
  return job.files || [];
};

const JobAccordionItem = ({
  summary,
  expanded,
  busy,
  actionError,
  onToggle,
  onCancel,
  onResumePending,
  onRestart,
  onRetryFailed,
  onRetryPageLimit,
}: {
  summary: AtsImportJobSummary;
  expanded: boolean;
  busy: boolean;
  actionError: string | null;
  onToggle: () => void;
  onCancel: () => void;
  onResumePending: () => void;
  onRestart: () => void;
  onRetryFailed: () => void;
  onRetryPageLimit: () => void;
}) => {
  const [job, setJob] = useState<AtsImportJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    void atsApi.getImportJob(summary.id)
      .then((detail) => {
        if (!cancelled) setJob(detail);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить детали импорта');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [expanded, summary.id, summary.status]);

  const files = useMemo(() => (job ? getJobFiles(job) : []), [job]);
  // An older API ignores the scoped retry query parameter and would retry every failure.
  const pageLimitFailedCount = summary.page_limit_failed_files ?? 0;
  const statusLabel = IMPORT_STATUS_LABELS[summary.status] || summary.status;
  const canCancel = CANCELABLE_IMPORT_STATUSES.has(summary.status);
  const canRestart = RESTARTABLE_IMPORT_STATUSES.has(summary.status);
  const canResumePending = ACTIVE_IMPORT_STATUSES.has(summary.status)
    || summary.processed_files < summary.total_files;
  const canRetryFailed = canRestart && summary.failed_files > 0;
  const canRetryPageLimit = !ACTIVE_IMPORT_STATUSES.has(summary.status)
    && canRetryFailed && pageLimitFailedCount > 0;

  return (
    <div className="overflow-hidden rounded-[16px] border border-[#E2E4E9] bg-white">
      <div className="flex items-start gap-3 px-5 py-4">
        <button
          type="button"
          onClick={onToggle}
          className="min-w-0 flex-1 text-left hover:opacity-90"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-[#23252D]">{formatDateTime(summary.created_at)}</span>
            <span className="rounded-full bg-[#F5F4EF] px-2.5 py-1 text-[12px] font-semibold text-[#525866]">
              {statusLabel}
            </span>
          </div>
          <div className="mt-2 text-[13px] text-[#525866]">
            Успешно: {summary.successful_files} / Ошибки: {summary.failed_files} / Всего: {summary.total_files}
          </div>
          {summary.progress_label && ACTIVE_IMPORT_STATUSES.has(summary.status) && (
            <div className="mt-1 text-[12px] text-[#23252D]">
              Этап: {summary.progress_label}
              {summary.progress_current != null && summary.progress_total != null
                ? ` (${summary.progress_current}/${summary.progress_total})`
                : null}
              {summary.progress_detail ? ` — ${summary.progress_detail}` : null}
            </div>
          )}
          {(summary.archive_file_name || summary.uploaded_by) && (
            <div className="mt-1 text-[12px] text-[#868C98]">
              {summary.archive_file_name ? `Архив: ${summary.archive_file_name}` : null}
              {summary.archive_file_name && summary.uploaded_by ? ' · ' : null}
              {summary.uploaded_by ? `Загрузил: ${summary.uploaded_by}` : null}
            </div>
          )}
          {summary.error_message && (
            <div className="mt-2 text-[12px] text-[#D92D20]">{summary.error_message}</div>
          )}
          {actionError && (
            <div className="mt-2 text-[12px] text-[#D92D20]">{actionError}</div>
          )}
        </button>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            {canCancel && (
              <button
                type="button"
                disabled={busy}
                onClick={onCancel}
                className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] hover:bg-[#F5F4EF] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Отменить
              </button>
            )}
            {canRetryFailed && (
              <button
                type="button"
                disabled={busy}
                onClick={onRetryFailed}
                className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] hover:bg-[#F5F4EF] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Повторить ошибки
              </button>
            )}
            {canRetryPageLimit && (
              <button
                type="button"
                disabled={busy}
                onClick={onRetryPageLimit}
                className="h-9 rounded-[8px] border border-[#E16349] bg-white px-3 text-[13px] font-semibold text-[#E16349] hover:bg-[#FFF4F1] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Повторить лимит страниц ({pageLimitFailedCount})
              </button>
            )}
            {canResumePending && (
              <button
                type="button"
                disabled={busy}
                onClick={onResumePending}
                className="h-9 rounded-[8px] border border-[#E16349] bg-white px-3 text-[13px] font-semibold text-[#E16349] hover:bg-[#FFF4F1] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Продолжить
              </button>
            )}
            {canRestart && (
              <button
                type="button"
                disabled={busy}
                onClick={onRestart}
                className="h-9 rounded-[8px] bg-[#E16349] px-3 text-[13px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Перезапустить
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#868C98] hover:bg-[#F5F4EF]"
            aria-label={expanded ? 'Свернуть' : 'Развернуть'}
          >
            <ChevronDownIcon className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#EEF0F2] px-5 py-4">
          {loading && <div className="text-[13px] text-[#868C98]">Загрузка файлов...</div>}
          {error && <div className="text-[13px] text-[#D92D20]">{error}</div>}
          {!loading && !error && files.length === 0 && (
            <div className="text-[13px] text-[#868C98]">Файлы не найдены</div>
          )}
          {!loading && !error && files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, index) => {
                const fileError = getFileError(file);
                const fileWarnings = getFileWarnings(file);
                const key = file.id || `${file.file_name}-${index}`;
                return (
                  <div key={key} className="rounded-[10px] bg-[#F8F7F3] px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-[#23252D]">{file.file_name}</span>
                      {file.status && (
                        <span className="text-[12px] text-[#525866]">{file.status}</span>
                      )}
                    </div>
                    {file.folder_path && (
                      <div className="mt-1 text-[12px] text-[#868C98]">Папка: {file.folder_path}</div>
                    )}
                    {fileError && (
                      <div className="mt-1 text-[12px] text-[#D92D20]">{fileError}</div>
                    )}
                    {fileWarnings.map((warning) => (
                      <div key={warning} className="mt-1 text-[12px] text-[#B54708]">{warning}</div>
                    ))}
                    {file.candidate_id && (
                      <Link
                        to={buildAtsCandidateLink(file.candidate_id, file.folder_path)}
                        className="mt-2 inline-flex text-[12px] font-semibold text-[#2F7BFF] hover:underline"
                      >
                        Открыть карточку кандидата
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {job && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void atsApi.downloadImportReport(job.id, 'csv')}
                className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] hover:bg-[#F5F4EF]"
              >
                Скачать CSV
              </button>
              <button
                type="button"
                onClick={() => void atsApi.downloadImportReport(job.id, 'xlsx')}
                className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] hover:bg-[#F5F4EF]"
              >
                Скачать XLSX
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function AtsImportLogsPanel() {
  const [items, setItems] = useState<AtsImportJobSummary[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadJobs = useCallback(async (nextPage: number, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await atsApi.listImportJobs({ page: nextPage, page_size: pageSize });
      setItems(response.items);
      setTotal(response.total);
      setPage(response.page);
    } catch {
      if (!silent) {
        setError('Не удалось загрузить логи импорта');
        setItems([]);
        setTotal(0);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadJobs(1);
  }, [loadJobs]);

  const hasActiveJobs = items.some((item) => ACTIVE_IMPORT_STATUSES.has(item.status));
  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const timer = window.setInterval(() => {
      void loadJobs(page, { silent: true });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveJobs, loadJobs, page]);

  const runJobAction = async (jobId: string, action: 'cancel' | 'resume-pending' | 'restart' | 'retry-failed' | 'retry-page-limit') => {
    if (action === 'restart'
      && !window.confirm(
        'Перезапуск удалит карточки, созданные этим импортом, и заново разберёт все файлы. Продолжить?',
      )
    ) {
      return;
    }
    if (action === 'retry-failed'
      && !window.confirm(
        'Повторно разберём только файлы с ошибкой. Успешные карточки не удаляются. Если импорт ещё идёт, текущая обработка остановится. Продолжить?',
      )
    ) {
      return;
    }
    if (action === 'retry-page-limit'
      && !window.confirm(
        'Повторно разберём только PDF-резюме, остановленные лимитом страниц. Остальные ошибки и успешные карточки останутся без изменений. Продолжить?',
      )
    ) {
      return;
    }
    if (action === 'resume-pending'
      && !window.confirm(
        'Продолжим только необработанные файлы. Успешные карточки и файлы с ошибкой останутся без изменений. Продолжить?',
      )
    ) {
      return;
    }
    setBusyJobId(jobId);
    setActionErrors((current) => {
      const next = { ...current };
      delete next[jobId];
      return next;
    });
    try {
      if (action === 'cancel') {
        await atsApi.cancelImportJob(jobId);
      } else if (action === 'resume-pending') {
        await atsApi.resumePendingImportJob(jobId);
      } else if (action === 'retry-failed') {
        await atsApi.retryFailedImportJob(jobId);
      } else if (action === 'retry-page-limit') {
        await atsApi.retryFailedImportJob(jobId, { onlyPageLimit: true });
      } else {
        await atsApi.restartImportJob(jobId);
      }
      await loadJobs(page, { silent: true });
    } catch {
      const messages = {
        cancel: 'Не удалось отменить импорт',
        'resume-pending': 'Не удалось продолжить необработанные файлы',
        restart: 'Не удалось перезапустить импорт',
        'retry-failed': 'Не удалось перезапустить файлы с ошибкой',
        'retry-page-limit': 'Не удалось перезапустить резюме с ошибкой лимита страниц',
      };
      setActionErrors((current) => ({
        ...current,
        [jobId]: messages[action],
      }));
    } finally {
      setBusyJobId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex h-full min-h-0 flex-col rounded-[24px] bg-[#ffffff] p-6">
      <div className="mb-6">
        <h2 className="text-[24px] font-medium text-[#000000]">Логи импорта</h2>
        <p className="mt-2 text-[14px] text-[#525866]">
          История массовых импортов резюме: статус джоба, количество успешных и ошибочных файлов.
          Зависший импорт можно продолжить с необработанных файлов. Файлы с ошибкой можно разобрать заново,
          не трогая уже созданные карточки. Полный перезапуск удаляет карточки этого импорта.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-[10px] bg-[#FFF2F0] p-3 text-[14px] text-[#D92D20]">{error}</div>
      )}

      {loading && <div className="text-[14px] text-[#868C98]">Загрузка...</div>}

      {!loading && !error && items.length === 0 && (
        <div className="text-[14px] text-[#868C98]">Импорты пока не выполнялись</div>
      )}

      {!loading && items.length > 0 && (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {items.map((item) => (
            <JobAccordionItem
              key={item.id}
              summary={item}
              expanded={expandedJobId === item.id}
              busy={busyJobId === item.id}
              actionError={actionErrors[item.id] || null}
              onToggle={() => setExpandedJobId((current) => (current === item.id ? null : item.id))}
              onCancel={() => void runJobAction(item.id, 'cancel')}
              onResumePending={() => void runJobAction(item.id, 'resume-pending')}
              onRestart={() => void runJobAction(item.id, 'restart')}
              onRetryFailed={() => void runJobAction(item.id, 'retry-failed')}
              onRetryPageLimit={() => void runJobAction(item.id, 'retry-page-limit')}
            />
          ))}
        </div>
      )}

      {!loading && total > pageSize && (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#EEF0F2] pt-4">
          <span className="text-[13px] text-[#868C98]">
            Страница {page} из {totalPages} · всего {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => void loadJobs(page - 1)}
              className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Назад
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => void loadJobs(page + 1)}
              className="h-9 rounded-[8px] border border-[#DDDCD6] bg-white px-3 text-[13px] font-semibold text-[#23252D] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Вперед
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
